import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Pager, usePaged } from '../components/Pager';
import { useCrumb } from '../lib/crumb';
import { DetailHead } from '../components/DetailHead';
import { Icon } from '../components/Icon';
import { mutationErrorMessage } from '../lib/gqlError';
import { statsText } from '../lib/runStats';
import { RefreshButton } from '../components/RefreshButton';

const JOB_DETAIL = gql`
  query JobDetail($name: String!) {
    jobDefinition(name: $name) {
      name
      handlerKey
      description
      scheduleCron
      enabled
      singleton
      timeoutMs
      params
      updatedBy
    }
    jobRuns(jobName: $name, limit: 30) {
      id
      status
      startedAt
      finishedAt
      triggeredBy
      error
      stats
      logs
    }
  }
`;
const RUN_NOW = gql`
  mutation RunNow($name: String!) {
    runJobNow(name: $name) {
      name
    }
  }
`;
const UPDATE_SCHEDULE = gql`
  mutation UpdateSchedule($name: String!, $scheduleCron: String, $params: String) {
    updateJobSchedule(name: $name, scheduleCron: $scheduleCron, params: $params) {
      name
      scheduleCron
      params
      updatedBy
    }
  }
`;

interface Run {
  id: string;
  status: string;
  startedAt: string;
  finishedAt?: string;
  triggeredBy: string;
  error?: string;
  stats?: string;
  logs?: string;
}
interface Def {
  name: string;
  handlerKey: string;
  description?: string;
  scheduleCron?: string;
  enabled: boolean;
  singleton: boolean;
  timeoutMs?: number;
  params: string;
  updatedBy?: string;
}
interface Data {
  jobDefinition: Def | null;
  jobRuns: Run[];
}

const STATUS_CLASS: Record<string, string> = {
  succeeded: 'confirmed',
  running: '',
  failed: 'inferred',
  skipped: 'inferred',
};

// The two operator-owned fields, and the key of the saveError that belongs to each.
type EditableKey = 'scheduleCron' | 'params';

export function SchedulerJobPage() {
  const { name = '' } = useParams();
  const { data, loading, error, refetch } = useQuery<Data>(JOB_DETAIL, {
    variables: { name },
    fetchPolicy: 'cache-and-network',
    pollInterval: 4000,
  });
  // `onError` is what makes the tuple's `error` reachable instead of an unhandled
  // rejection: with no handler at all a Forbidden/expired-jwt refusal rendered
  // nothing and the click read as a no-op (e-39). The message is read from the
  // tuple below and shown next to the button that was pressed.
  const [runNow, { loading: running, error: runError }] = useMutation(RUN_NOW, {
    onCompleted: () => refetch(),
    onError: () => {},
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Keyed by field: the two EditableFields below share one saveEdit, so an
  // untagged message would render under BOTH — a rejected cron would also light
  // up Params, which the operator never touched.
  const [saveError, setSaveError] = useState<{ field: EditableKey; message: string } | null>(null);
  const [updateSchedule, { loading: saving }] = useMutation(UPDATE_SCHEDULE);

  // The server owns validation (a bad cron would throw inside the worker's reconcile),
  // so surface its message verbatim rather than guessing at rules here. Resolves to
  // true only on success, which is what tells the field to leave edit mode.
  const saveEdit = async (field: EditableKey, value: string): Promise<boolean> => {
    setSaveError(null);
    try {
      await updateSchedule({ variables: { name, [field]: value } });
      await refetch();
      return true;
    } catch (err) {
      // Same extraction as the buttons: a 403 body carries the reason, `message`
      // alone carries only "Received status code 403".
      setSaveError({ field, message: mutationErrorMessage(err as Error) ?? 'Request failed.' });
      return false;
    }
  };

  const runErrMsg = mutationErrorMessage(runError);
  const runs = data?.jobRuns ?? [];
  // Default-select the latest run once loaded.
  useEffect(() => {
    if (!selectedId && runs.length) setSelectedId(runs[0].id);
  }, [runs, selectedId]);
  const selected = runs.find((r) => r.id === selectedId) ?? runs[0];
  const pg = usePaged(runs);

  // Breadcrumb tail — the topbar knows the route, not the entity.
  // MUST sit above the early returns: called after them, it would be a
  // conditional hook and React would throw once the data arrived.
  useCrumb(data?.jobDefinition?.name);

  if (loading && !data) return <div className="page">Loading…</div>;
  if (error) return <div className="page muted">Failed to load: {error.message}</div>;
  const def = data?.jobDefinition;
  if (!def)
    return (
      <div className="page">
        <p className="muted">Job “{name}” not found.</p>
        <Link to="/scheduler">← Back to scheduler</Link>
      </div>
    );

  const facts: [string, React.ReactNode][] = [
    ['Handler', def.handlerKey],
    ['Enabled', def.enabled ? 'on' : 'off'],
    ['Singleton', def.singleton ? 'yes' : 'no'],
    ['Timeout', def.timeoutMs ? `${def.timeoutMs / 1000}s` : '—'],
    ['Updated by', def.updatedBy ?? '—'],
  ];

  // '' means "no Stats block". Two runs hit it: one that wrote no stats at all, and
  // one whose NULL stats column the api coalesces to `{}` before serialising it
  // (jobs.service.ts: `JSON.stringify(r.stats ?? {})`). Without the guard both would
  // render an empty block under a Stats label.
  const selectedStats = statsText(selected?.stats);

  return (
    <div className="page">
      <div className="detail-back">
        <Link to="/scheduler" className="muted">
          ← Scheduler
        </Link>
      </div>
      <DetailHead
        name={def.name}
        badge={
          <span className={`badge ${def.enabled ? 'confirmed' : 'inferred'}`}>
            {def.enabled ? 'enabled' : 'disabled'}
          </span>
        }
        facts={[
          def.scheduleCron && <span className="font-mono">{def.scheduleCron}</span>,
          def.handlerKey,
          def.singleton && 'single-instance',
          def.updatedBy && `configured by ${def.updatedBy}`,
        ]}
        /* A job is not a scanned entity — its freshness IS its last run, and the
           runs table below is the authoritative view of that. */
        curated
        scanLead="Not a scanned entity — this job's freshness is its last run."
        scanNote="Run history is below."
        actions={
          <>
            <button className="mini-btn" disabled={running} onClick={() => runNow({ variables: { name } })}>
              <Icon name="run" /> Run now
            </button>
            {runErrMsg && (
              <div className="run-error" role="alert" style={{ marginTop: 6, maxWidth: 360 }}>
                {runErrMsg}
              </div>
            )}
          </>
        }
      />
      {/* Full text here — the list clamps it. This is the page you land on before
          hitting Run now, so it is the one that has to answer "what will this do?". */}
      {def.description && <p className="detail-desc">{def.description}</p>}

      <h3 className="section">Configuration</h3>
      {/* Schedule + Params are the two operator-owned fields — the seed never
          overwrites them, so this page is the ONLY way to change them. They were
          read-only here while `updateJobSchedule` sat unused in the API, which made
          the cron effectively unchangeable in a running environment. */}
      <EditableField
        label="Schedule"
        value={def.scheduleCron ?? ''}
        hint="5-field cron, UTC — e.g. 0,30 * * * * for :00 and :30"
        onSave={(v) => saveEdit('scheduleCron', v)}
        disabled={saving}
        error={saveError?.field === 'scheduleCron' ? saveError.message : null}
      />
      <div className="kv-grid" style={{ marginTop: 10 }}>
        {facts.map(([k, v]) => (
          <div className="kv" key={k}>
            <div className="kv-k">{k}</div>
            <div className="kv-v">{v}</div>
          </div>
        ))}
      </div>
      <EditableField
        label="Params"
        value={prettyJson(def.params)}
        hint='JSON object passed to the handler — e.g. {"quorum":0.3}'
        multiline
        onSave={(v) => saveEdit('params', v)}
        disabled={saving}
        error={saveError?.field === 'params' ? saveError.message : null}
      />

      <div className="section-head">
        <h3>Runs ({runs.length})</h3>
        <span className="spacer" />
        <RefreshButton refetch={() => refetch().catch(() => undefined)} />
      </div>
      {runs.length === 0 ? (
        <p className="muted">No runs yet. Hit “Run now”.</p>
      ) : (
        <>
        <div className="run-layout">
          <table className="run-list">
            <thead>
              <tr>
                <th>Started</th>
                <th>Status</th>
                <th>Trigger</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {pg.pageItems.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  className={r.id === selected?.id ? 'selected-run' : ''}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="muted">{new Date(r.startedAt).toLocaleTimeString()}</td>
                  <td>
                    <span className={`badge ${STATUS_CLASS[r.status] ?? ''}`}>{r.status}</span>
                  </td>
                  <td className="muted">{r.triggeredBy}</td>
                  <td className="muted">{duration(r.startedAt, r.finishedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {selected && (
            <div className="run-detail">
              <div className="muted text-xs" style={{ marginBottom: 6 }}>
                run #{selected.id} · {new Date(selected.startedAt).toLocaleString()} ·{' '}
                <span className={`badge ${STATUS_CLASS[selected.status] ?? ''}`}>{selected.status}</span>
              </div>
              {selected.error && <div className="run-error">{selected.error}</div>}
              {/* What the handler counted — the only place a degradation marker
                  (cdn-cost's objects_truncated_files / dropped_object_keys) reaches
                  an operator. Hidden when the run recorded nothing, so jobs that
                  report no stats keep the panel as it was. */}
              {selectedStats && (
                <>
                  <div className="muted text-xs" style={{ marginBottom: 4 }}>
                    Stats
                  </div>
                  <div className="log-viewer" style={{ marginBottom: 8 }}>
                    <pre>{selectedStats}</pre>
                  </div>
                </>
              )}
              <div className="log-viewer">
                <pre>{selected.logs?.trim() || '(no logs captured)'}</pre>
              </div>
            </div>
          )}
        </div>
        <Pager page={pg.page} pageCount={pg.pageCount} total={pg.total} start={pg.start} pageSize={pg.pageSize} onPage={pg.setPage} />
        </>
      )}
    </div>
  );
}

/**
 * Read-only until you click Edit — these two fields change what runs in production,
 * so an always-live input next to a table of green runs is too easy to fat-finger.
 * Save stays disabled until the value actually differs, and the server's rejection
 * is shown in place with the edit still open, so a typo is one keystroke from fixed
 * rather than retyped from scratch.
 */
function EditableField({
  label,
  value,
  hint,
  multiline,
  disabled,
  error,
  onSave,
}: {
  label: string;
  value: string;
  hint: string;
  multiline?: boolean;
  disabled?: boolean;
  error?: string | null;
  onSave: (v: string) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const start = () => {
    setDraft(value);
    setEditing(true);
  };
  const commit = async () => {
    if (await onSave(draft)) setEditing(false); // stay open on rejection
  };

  if (!editing)
    return (
      <div className="kv" style={{ marginTop: 10 }}>
        <div className="kv-k">{label}</div>
        <div className="kv-v" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <code className="text-xs">{value || '—'}</code>
          <button className="mini-btn" onClick={start}>
            Edit
          </button>
        </div>
      </div>
    );

  return (
    <div className="kv" style={{ marginTop: 10 }}>
      <div className="kv-k">{label}</div>
      <div className="kv-v">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
          {multiline ? (
            <textarea
              className="kv-edit__input"
              rows={3}
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              style={{ minWidth: 320 }}
            />
          ) : (
            <input
              className="kv-edit__input"
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              // Enter saves, Escape cancels — a one-line cron should not need the mouse.
              onKeyDown={(e) => {
                if (e.key === 'Enter') void commit();
                if (e.key === 'Escape') setEditing(false);
              }}
              style={{ minWidth: 220 }}
            />
          )}
          <button className="mini-btn" disabled={disabled || draft === value} onClick={() => void commit()}>
            {disabled ? 'Saving…' : 'Save'}
          </button>
          <button className="mini-btn" disabled={disabled} onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
        <div className="muted text-2xs" style={{ marginTop: 4 }}>
          {hint}
        </div>
        {error && (
          <div className="run-error" role="alert" style={{ marginTop: 6 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function duration(start: string, end?: string): string {
  if (!end) return '…';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}
function prettyJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s));
  } catch {
    return s;
  }
}
